import { bound } from "@frui.ts/helpers";
import { Router, ScreenBase, watchBusy } from "@frui.ts/screens";
import BusyWatcher from "@frui.ts/screens/dist/structure/busyWatcher";
import { attachAutomaticValidator, validate } from "@frui.ts/validation";
import { Modules, newNavigationName } from "config";
import type { interfaces } from "inversify";
import { computed, observable } from "mobx";
import ExternalIdentityProvider from "entities/externalIdentityProvider";
import ExternalUser from "../../../entities/externalUser";
import UserRepository from "../../../repositories/userRepository";
import EventBus from "../../../services/eventBus";
import LocalizationService from "../../../services/localizationService";
import NotificationService, { NotificationType } from "../../../services/notificationService";
import OptionsService from "services/optionsService";
import SecurityService from "../../../services/securityService";

const navigationName = "identity-providers";

const UserIdentityProvidersValidationRules: any = {
  allowedProviders: {
    required: true,
  },
};

@Router.registerRoute({ name: Router.Self, route: newNavigationName })
export default class UserIdentityProvidersViewModel extends ScreenBase {
  @observable allowedProviders: string[] = [];
  @observable busyWatcher = new BusyWatcher();

  constructor(
    private user: ExternalUser,
    private repository: UserRepository,
    private localizationService: LocalizationService,
    private notificationService: NotificationService,
    private securityService: SecurityService,
    private eventBus: EventBus,
    private optionsService: OptionsService
  ) {
    super();
    this.navigationName = navigationName;
  }

  @computed
  get name() {
    return this.translate("name");
  }

  @computed
  get allowWrite() {
    return this.securityService.allowToEntity("userRole", "write");
  }

  @watchBusy
  protected async onInitialize() {
    this.allowedProviders = (await this.repository.getAllIdentityProviders(this.user)).items;
    attachAutomaticValidator(this, UserIdentityProvidersValidationRules);

    return super.onInitialize();
  }

  @computed
  get externalIdentityProviderOptions() {
    return this.optionsService.entityOptions(ExternalIdentityProvider);
  }

  @bound
  @watchBusy
  async onSubmit() {
    if (validate(this) && this.securityService.allowToEntity("user", "write")) {
      try {
        await this.repository.updateAllowedIdentityProviders(this.user, this.allowedProviders);

        this.notificationService.add({
          title: this.translate("updated").replace("%username%", this.user.username),
          type: NotificationType.SUCCESS,
        });
      } catch (e) {}
    }
  }

  translate(code: string) {
    return this.localizationService.translateModule(Modules.users, `users.userIdentityProviders.${code}`);
  }

  static Factory({ container }: interfaces.Context) {
    return (user: ExternalUser) => {
      return new UserIdentityProvidersViewModel(
        user,
        container.get(UserRepository),
        container.get(LocalizationService),
        container.get(NotificationService),
        container.get(SecurityService),
        container.get(EventBus),
        container.get(OptionsService)
      );
    };
  }
}
